import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import blogService from "../../services/blogService";
import { tourService } from "../../services/tourService";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useSelector } from "react-redux";
import axios from "../../config/axiosInstance";
import { toast } from "react-toastify";

const BlogCreate = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  let userId = 0;
  if (user) {
    try {
      const decoded = jwtDecode(user);
      userId = decoded.sub || 0;
    } catch (error) {
      toast.error("Lỗi khi giải mã token");
    }
  }

  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [blogImages, setBlogImages] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const toursData = await tourService.getAllTours({
          page: 1,
          pageSize: 10,
        });
        if (!toursData || !Array.isArray(toursData.data))
          throw new Error("Dữ liệu Tour không hợp lệ");
        setTours(toursData.data);
      } catch (error) {
        console.error("Lỗi khi lấy Tour:", error);
        setTours([]);
      }
    };
    fetchTours();
  }, []);

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newImages = Array.from(files).map((file) => ({
      url: URL.createObjectURL(file),
      file,
      id: Date.now() + Math.random(),
    }));

    setBlogImages([...blogImages, ...newImages]);
  };

  const removeImage = (id) => {
    setBlogImages(blogImages.filter((image) => image.id !== id));
    const imageToRemove = blogImages.find((image) => image.id === id);
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.url);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errors = {};

    if (!selectedTour) errors.Tour = "Vui lòng chọn Tour";
    if (!title.trim()) errors.Title = "Tiêu đề không được để trống";
    if (!content.trim()) errors.Content = "Nội dung không được để trống";
    if (blogImages.length === 0) errors.BlogImages = "Cần ít nhất một ảnh";

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const uploadedImageUrls = await Promise.all(
        blogImages.map(async (image) => {
          const formData = new FormData();
          formData.append("files", image.file);
          const response = await axios.post(
            "/uploadvu/upload-images",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          return response.data.images[0].url;
        })
      );

      const blogData = {
        Name: title,
        Description: content,
        AuthorId: userId,
        TourId: selectedTour,
        IsFeatured: false,
        ImageUrls: uploadedImageUrls,
      };

      await blogService.createBlog(blogData);
      toast.success("Blog đã được tạo thành công! Vui lòng chờ admin duyệt");
      navigate("/my-blog");
    } catch (error) {
      console.error("Lỗi khi tạo blog:", error.message);
      toast.error(
        error.response?.data?.message ||
          "Lỗi khi tạo blog, vui lòng thử lại sau."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen px-4 py-8 bg-gray-50">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full h-full">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Tạo Bài Viết Mới
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tour Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chọn Tour
            </label>
            <select
              className={`w-full px-4 py-3 rounded-xl border ${
                validationErrors.Tour ? "border-red-300" : "border-gray-300"
              } focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200`}
              value={selectedTour}
              onChange={(e) => setSelectedTour(e.target.value)}
            >
              <option value="">-- Chọn Tour --</option>
              {tours.map((tour) => (
                <option key={tour.tourId} value={tour.tourId}>
                  {tour.tourName}
                </option>
              ))}
            </select>
            {validationErrors.Tour && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.Tour}
              </p>
            )}
          </div>

          {/* Title Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiêu đề
            </label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={title}
                onChange={(event, editor) => {
                  setTitle(editor.getData());
                }}
                config={{
                  toolbar: ["heading", "|", "bold", "italic"],
                }}
              />
            </div>
            {validationErrors.Title && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.Title}
              </p>
            )}
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nội dung
            </label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={content}
                onChange={(event, editor) => {
                  setContent(editor.getData());
                }}
              />
            </div>
            {validationErrors.Content && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.Content}
              </p>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hình ảnh
            </label>
            <div className="relative">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  validationErrors.BlogImages
                    ? "border-red-300"
                    : "border-gray-300"
                } file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors duration-200`}
              />
            </div>
            {validationErrors.BlogImages && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.BlogImages}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Bạn có thể chọn nhiều ảnh cùng lúc
            </p>
          </div>

          {/* Image Preview with Delete Option */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ảnh đã chọn
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {blogImages.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  Chưa có ảnh nào được chọn
                </p>
              ) : (
                blogImages.map((image) => (
                  <div
                    key={image.id}
                    className="relative group rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <img
                      src={image.url}
                      alt="Preview"
                      className="w-full h-40 object-cover"
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                      onClick={() => removeImage(image.id)}
                      aria-label="Xóa ảnh"
                      // style={{ zIndex: 10 }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-2 rounded-xl hover:bg-blue-500 transition-colors duration-300 flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Đang tải lên...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane"></i>
                  Đăng bài
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogCreate;
